<script setup>
// shadcn "section card": muted title with an icon, a large number and a
// short description. `progress` (0–100) adds a thin bar under the number.
defineProps({
  title: { type: String, required: true },
  icon: { type: String, required: true },
  value: { type: [Number, String], default: 0 },
  description: { type: String, default: '' },
  progress: { type: Number, default: null },
  tone: { type: String, default: 'var(--brand)' }, // bar colour
  loading: { type: Boolean, default: false },
});
</script>

<template>
  <section class="stat">
    <header class="stat__head">
      <span class="stat__title">{{ title }}</span>
      <v-icon size="16" class="stat__icon">{{ icon }}</v-icon>
    </header>
    <div class="stat__value">
      <span v-if="loading" class="stat__skeleton" aria-label="Loading" />
      <template v-else>{{ value }}</template>
    </div>
    <div v-if="progress !== null && !loading" class="stat__bar" aria-hidden="true">
      <span :style="{ width: `${Math.min(100, Math.max(0, progress))}%`, background: tone }" />
    </div>
    <p v-if="description" class="stat__description">
      <slot name="description">{{ description }}</slot>
    </p>
  </section>
</template>

<style scoped>
.stat {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 20px 24px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.stat__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.stat__title {
  font-size: 14px;
  font-weight: 500;
  color: var(--muted-foreground);
}
.stat__icon {
  color: var(--muted-foreground);
}
.stat__value {
  min-height: 36px;
  font-size: 28px;
  font-weight: 600;
  line-height: 36px;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: var(--foreground);
}
.stat__skeleton {
  display: inline-block;
  width: 72px;
  height: 24px;
  border-radius: 6px;
  background: var(--accent);
  vertical-align: middle;
  animation: pulse 1.4s ease-in-out infinite;
}
.stat__bar {
  height: 6px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--accent);
}
.stat__bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  transition: width 0.4s ease;
}
.stat__description {
  margin: 0;
  font-size: 13px;
  color: var(--muted-foreground);
}
@keyframes pulse {
  50% {
    opacity: 0.5;
  }
}
@media (prefers-reduced-motion: reduce) {
  .stat__skeleton {
    animation: none;
  }
}
</style>
